/**
 * Dynamic Date Range Configuration Models
 *
 * These interfaces enable JSON date range configurations
 * that can be persisted and restored, automatically recalculating
 * concrete dates based on relative definitions.
 */

/**
 * Time unit for relative date calculations
 */
export type DateRangeUnit = 'day' | 'week' | 'month' | 'quarter' | 'year';

/**
 * Reference point for relative date calculations
 */
export type DateRangeReference =
  | 'today'
  | 'startOfWeek'
  | 'endOfWeek'
  | 'startOfMonth'
  | 'endOfMonth'
  | 'startOfQuarter'
  | 'endOfQuarter'
  | 'startOfYear'
  | 'endOfYear';

/**
 * Type of date range configuration
 */
export enum DateRangeConfigType {
  /** Built-in preset (matches existing presets like last30days, thisMonth) */
  Preset = 'preset',
  /** Relative offset from today (e.g., "today minus 30 days") */
  RelativeOffset = 'relative_offset',
  /** Relative to a reference point (e.g., "start of last quarter") */
  RelativeReference = 'relative_reference',
  /** Fixed/concrete dates (custom range) */
  Fixed = 'fixed',
}

/**
 * Preset identifiers for common date ranges
 */
export type DateRangePreset =
  | 'today'
  | 'yesterday'
  | 'last7days'
  | 'last30days'
  | 'last60days'
  | 'last90days'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisQuarter'
  | 'lastQuarter'
  | 'thisYear'
  | 'lastYear'
  | 'yearToDate'
  | 'monthToDate'
  | 'weekToDate';

/**
 * Base interface for all date range configurations
 */
export interface BaseDateRangeConfig {
  /** Unique identifier for this configuration */
  id?: string;
  /** Display label for the date range */
  label: string;
  /** Type of configuration */
  type: DateRangeConfigType;
}

export interface PresetDateRangeConfig extends BaseDateRangeConfig {
  type: DateRangeConfigType.Preset;
  /** Preset identifier */
  preset: DateRangePreset;
}

export interface RelativeOffsetDateRangeConfig extends BaseDateRangeConfig {
  type: DateRangeConfigType.RelativeOffset;
  /** Start date offset from today (negative = past, positive = future) */
  startOffset: number;
  /** Unit for start date offset */
  startUnit: DateRangeUnit;
  /** End date offset from today (0 = today) */
  endOffset: number;
  /** Unit for end date offset */
  endUnit: DateRangeUnit;
}


export interface RelativeReferenceDateRangeConfig extends BaseDateRangeConfig {
  type: DateRangeConfigType.RelativeReference;
  /** Reference point for start date */
  startReference: DateRangeReference;
  /** Period offset from start reference (e.g., -1 for "last" period) */
  startPeriodOffset?: number;
  /** Reference point for end date */
  endReference: DateRangeReference;
  /** Period offset from end reference */
  endPeriodOffset?: number;
}

/**
 * Fixed/concrete date range configuration
 * For custom date selections that should be persisted as-is
 *
 * @example
 * {
 *   type: 'fixed',
 *   label: 'January 2024',
 *   startDate: '2024-01-01',
 *   endDate: '2024-01-31'
 * }
 */
export interface FixedDateRangeConfig extends BaseDateRangeConfig {
  type: DateRangeConfigType.Fixed;
  /** Start date as ISO string (YYYY-MM-DD) */
  startDate: string;
  /** End date as ISO string (YYYY-MM-DD) */
  endDate: string;
}

/**
 * Union type for all date range configurations
 * This is the main type to use for serialization/deserialization
 */
export type DynamicDateRangeConfig =
  | PresetDateRangeConfig
  | RelativeOffsetDateRangeConfig
  | RelativeReferenceDateRangeConfig
  | FixedDateRangeConfig;

/**
 * Resolved date range with concrete dates
 */
export interface ResolvedDateRange {
  /** Resolved start date */
  startDate: Date;
  /** Resolved end date */
  endDate: Date;
  /** Original configuration used to resolve */
  config: DynamicDateRangeConfig;
}

/**
 * Configuration for the date range filter component
 * Includes both the active selection and available options
 */
export interface DateRangeFilterConfig {
  /** Currently selected date range configuration */
  selectedConfig?: DynamicDateRangeConfig;
  /** Available preset options (optional, uses defaults if not provided) */
  availableOptions?: DynamicDateRangeConfig[];
  /** Allow custom date selection */
  allowCustomRange?: boolean;
}

/**
 * Default preset configurations for common date ranges
 */
export const DEFAULT_DATE_RANGE_PRESETS: PresetDateRangeConfig[] = [
  { type: DateRangeConfigType.Preset, preset: 'today', label: 'Today' },
  { type: DateRangeConfigType.Preset, preset: 'yesterday', label: 'Yesterday' },
  { type: DateRangeConfigType.Preset, preset: 'last7days', label: 'Last 7 Days' },
  { type: DateRangeConfigType.Preset, preset: 'last30days', label: 'Last 30 Days' },
  { type: DateRangeConfigType.Preset, preset: 'lastMonth', label: 'Last Month' },
  { type: DateRangeConfigType.Preset, preset: 'thisMonth', label: 'This Month' },
  { type: DateRangeConfigType.Preset, preset: 'monthToDate', label: 'Month to Date' },
  { type: DateRangeConfigType.Preset, preset: 'yearToDate', label: 'Year to Date' },
];
